// src/components/personal/sections/OrdersSection/hooks/useOrderDataCore.ts
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Order, CacheInfo } from '../types/types';
import { orderCache } from '../utils/orderCache';
import { 
  ERROR_CODES, 
  getErrorMessageStatic, 
  logError, 
  shouldRetry 
} from '../error/error-index';

// Объявление глобальной переменной
declare global {
  interface Window {
    __ACTIVE_FILTERS_CHECK: () => boolean;
    __FILTERS_CHANGED_CALLBACK: ((isFiltering: boolean) => void) | null;
    __BLOCK_ORDER_PAGE_LOAD: boolean;
  }
}

// Инициализация глобальных переменных
if (typeof window !== 'undefined') {
  window.__ACTIVE_FILTERS_CHECK = () => false;
  window.__FILTERS_CHANGED_CALLBACK = null;
  window.__BLOCK_ORDER_PAGE_LOAD = false;
}

// Вспомогательные функции
export function buildOrderApiUrl(businessId: number | string, offset: number, limit: number): string {
  return `/api/orders/all/?business=${businessId}&offset=${offset}&limit=${limit}`;
}

// APIError для обработки ошибок API
class APIError extends Error {
  code: string;
  details?: Record<string, any>;
  
  constructor({ code, message, details }: { 
    code: string; 
    message: string; 
    details?: Record<string, any> 
  }) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.details = details;
  }
}

// Интерфейс возвращаемого значения хука
interface UseOrderDataCoreReturn {
  // Состояние данных
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  allOrdersLoaded: boolean;
  totalOrdersCount: number;
  
  // Методы для загрузки
  loadAllOrders: () => Promise<void>;
  loadOrderPage: (offset: number, limit: number) => Promise<void>;
  refreshOrders: (forceRefresh?: boolean, forceAllOrders?: boolean) => Promise<void>;
  exportOrdersToCSV: (ordersToExport: Order[]) => Promise<boolean>;
  
  // Методы работы с кешем
  clearCache: () => void;
  cacheInfo: CacheInfo | null;
}

export const useOrderDataCore = (initialOrders: Order[] = []): UseOrderDataCoreReturn => {
  const { t } = useTranslation();
  const { businessId } = useBusinessId();
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние данных
  const [orders, setOrders] = useState<Order[]>(initialOrders);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);
  const [totalOrdersCount, setTotalOrdersCount] = useState<number>(0);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState<boolean>(false);
  const [isActionLocked, setIsActionLocked] = useState(false);
  
  // Refs для отслеживания состояний
  const isLoadingRef = useRef(false);
  const initialLoadCompletedRef = useRef(false);
  const currentRequestIdRef = useRef<string | null>(null);
  const retryCountRef = useRef<Record<string, number>>({});
  
  // Обработка ошибок API
  const handleApiError = useCallback((error: any, context: string) => {
    // Преобразуем любую ошибку в APIError
    let apiError: APIError;
    
    if (error instanceof APIError) {
      apiError = error;
    } else if (error instanceof Error) {
      apiError = new APIError({
        code: error.name === 'TypeError' && error.message.includes('fetch') 
          ? ERROR_CODES.NETWORK_ERROR 
          : ERROR_CODES.UNKNOWN_ERROR,
        message: error.message,
        details: { stack: error.stack }
      });
    } else {
      apiError = new APIError({
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: typeof error === 'string' ? error : 'Неизвестная ошибка',
        details: { originalError: error }
      });
    }
    
    // Логируем ошибку
    logError(apiError, context);
    
    // Устанавливаем сообщение об ошибке для пользователя
    const userMessage = getErrorMessageStatic(apiError, t);
    setError(userMessage);
    
    return {
      error: apiError,
      shouldRetry: shouldRetry(apiError),
      message: userMessage
    };
  }, [t]);
  
  // Генерация уникального ID запроса
  const generateRequestId = useCallback(() => {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  
  // Функция для повторных попыток запроса
  const retryRequest = useCallback(<T extends (...args: any[]) => Promise<any>>(
    requestFn: T,
    requestId: string,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<ReturnType<T>> => {
    return new Promise((resolve, reject) => {
      const attempt = async () => {
        try {
          const result = await requestFn();
          retryCountRef.current[requestId] = 0;
          resolve(result);
        } catch (error) {
          const { shouldRetry: shouldRetryError } = handleApiError(error, `запрос (попытка ${retryCountRef.current[requestId] || 0})`);
          
          retryCountRef.current[requestId] = (retryCountRef.current[requestId] || 0) + 1;
          
          if (shouldRetryError && retryCountRef.current[requestId] <= maxRetries) {
            if (DEBUG) console.log(`Повторная попытка ${retryCountRef.current[requestId]}/${maxRetries} через ${delay}мс`);
            setTimeout(attempt, delay);
          } else {
            delete retryCountRef.current[requestId];
            reject(error);
          }
        }
      };
      
      retryCountRef.current[requestId] = 1;
      attempt();
    });
  }, [handleApiError, DEBUG]);
  
  // Очистка кеша
  const clearCache = useCallback(() => {
    orderCache.clearCache();
    if (DEBUG) console.log('Кеш очищен');
  }, [DEBUG]);
  
  // Загрузка всех заказов
  const loadAllOrders = useCallback(async () => {
    // Базовые проверки
    if (!businessId || isLoadingRef.current) {
      if (DEBUG) console.log('loadAllOrders: загрузка невозможна - уже выполняется или нет businessId');
      return;
    }
    
    // Проверка кеша
    if (orderCache.hasValidCache(businessId)) {
      const cachedOrders = orderCache.getOrders(businessId);
      if (cachedOrders && cachedOrders.length > 0) {
        if (DEBUG) console.log(`Используем ${cachedOrders.length} заказов из кэша`);
        setOrders(cachedOrders);
        setAllOrdersLoaded(true);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        setTotalOrdersCount(cachedOrders.length);
        return;
      }
    }
    
    // Если все заказы уже загружены и нет ошибок, используем их
    if (allOrdersLoaded && orders.length > 0 && !error) {
      if (DEBUG) console.log('Все заказы уже загружены, используем существующие данные');
      return;
    }
    
    // Создаем ID запроса и устанавливаем состояние загрузки
    const requestId = generateRequestId();
    currentRequestIdRef.current = requestId;
    isLoadingRef.current = true;
    setIsActionLocked(true);
    setIsLoading(true);
    setError(null);
    
    try {
      // Запрос на количество заказов
      const fetchCountRequest = async () => {
        const response = await api.get(buildOrderApiUrl(businessId, 0, 1));
        
        if (currentRequestIdRef.current !== requestId) {
          throw new Error('Запрос отменен');
        }
        
        // Обработка различных форматов ответа API
        let total = 0;
        if (response && typeof response === 'object') {
          if ('count' in response) {
            total = response.count;
          } else if ('total' in response) {
            total = response.total;
          } else if ('totalItems' in response) {
            total = response.totalItems;
          }
        }
        
        if (!total) {
          throw new APIError({
            code: ERROR_CODES.UNKNOWN_ERROR,
            message: 'Не удалось получить количество заказов'
          });
        }
        
        return total;
      };
      
      // Получаем общее количество заказов
      const totalCount = await retryRequest(fetchCountRequest, `${requestId}_count`);
      setTotalOrdersCount(totalCount);
      
      // Настраиваем параметры пакетной загрузки
      const maxOrdersToLoad = Math.min(totalCount, 5000);
      const batchSize = 500;
      const batches = Math.ceil(maxOrdersToLoad / batchSize);
      
      if (DEBUG) console.log(`Загружаем все заказы (${totalCount}), в ${batches} пакетах`);
      
      // Временный массив для результатов
      let tempOrders: Order[] = [];
      
      // Загружаем по пакетам
      for (let i = 0; i < batches; i++) {
        if (currentRequestIdRef.current !== requestId) {
          if (DEBUG) console.log('Запрос отменен во время пакетной загрузки');
          return;
        }
        
        const offset = i * batchSize;
        const limit = Math.min(batchSize, maxOrdersToLoad - offset);
        
        if (DEBUG) console.log(`Загрузка пакета ${i+1}/${batches}: ${offset}-${offset+limit} из ${totalCount}`);
        
        const fetchBatchRequest = async () => {
          return await api.get(buildOrderApiUrl(businessId, offset, limit));
        };
        
        const batchResponse = await retryRequest(
          fetchBatchRequest, 
          `${requestId}_batch_${i}`,
          2
        );
        
        // Обработка различных форматов ответа API
        let batchOrders: Order[] = [];
        
        if (batchResponse && typeof batchResponse === 'object') {
          if (Array.isArray(batchResponse)) {
            batchOrders = batchResponse;
          } else if ('results' in batchResponse && Array.isArray(batchResponse.results)) {
            batchOrders = batchResponse.results;
          } else if ('data' in batchResponse && Array.isArray(batchResponse.data)) {
            batchOrders = batchResponse.data;
          }
        }
        
        if (batchOrders.length > 0) {
          tempOrders = [...tempOrders, ...batchOrders];
        } else {
          console.warn('Неожиданный формат данных при загрузке заказов:', batchResponse);
          break;
        }
      }
      
      if (currentRequestIdRef.current !== requestId) {
        if (DEBUG) console.log('Запрос отменен после пакетной загрузки');
        return;
      }
      
      if (tempOrders.length > 0) {
        if (DEBUG) console.log(`Загружено ${tempOrders.length} заказов из ${totalCount}`);
        
        setOrders(tempOrders);
        setAllOrdersLoaded(true);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        
        // Сохраняем в кеш
        orderCache.setOrders(tempOrders, businessId);
        if (DEBUG) console.log(`Сохранено ${tempOrders.length} заказов в кэш`);
      }
    } catch (error: any) {
      handleApiError(error, 'loadAllOrders');
    } finally {
      if (currentRequestIdRef.current === requestId) {
        currentRequestIdRef.current = null;
      }
      
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsActionLocked(false);
      }, 300);
    }
  }, [businessId, allOrdersLoaded, orders.length, error, generateRequestId, retryRequest, handleApiError, DEBUG]);
  
  // Загрузка страницы заказов
  const loadOrderPage = useCallback(async (offset: number, limit: number) => {
    // Явно проверяем флаг блокировки в начале функции
    if (window.__BLOCK_ORDER_PAGE_LOAD) {
      if (DEBUG) console.log('loadOrderPage: загрузка страницы заблокирована из-за активных фильтров/поиска');
      return;
    }
    
    if (!businessId || isLoadingRef.current) {
      if (DEBUG) console.log('loadOrderPage: загрузка невозможна - уже выполняется или нет businessId');
      return;
    }
    
    // Дополнительная проверка на активные фильтры/поиск через глобальную функцию
    const hasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
    if (hasActiveFilters) {
      if (DEBUG) console.log('loadOrderPage: загрузка страницы заблокирована из-за активных фильтров (проверка через __ACTIVE_FILTERS_CHECK)');
      return;
    }
    
    // Проверка кеша
    if (orderCache.hasValidCache(businessId)) {
      const cachedOrders = orderCache.getOrders(businessId);
      if (cachedOrders && cachedOrders.length > 0) {
        if (DEBUG) console.log(`Используем ${cachedOrders.length} заказов из кэша для страницы`);
        
        // Используем кеш для получения подмножества данных
        const endIndex = Math.min(offset + limit, cachedOrders.length);
        const pageOrders = cachedOrders.slice(offset, endIndex);
        
        setOrders(pageOrders);
        setAllOrdersLoaded(false); // Не все заказы загружены, только страница
        setTotalOrdersCount(cachedOrders.length);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        
        return;
      }
    }
    
    // Создаем ID запроса и устанавливаем состояние загрузки
    const requestId = generateRequestId();
    currentRequestIdRef.current = requestId;
    isLoadingRef.current = true;
    setIsActionLocked(true);
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchPageRequest = async () => {
        const url = buildOrderApiUrl(businessId, offset, limit);
        if (DEBUG) console.log(`Загрузка страницы, смещение=${offset}, размер=${limit}`);
        
        return await api.get(url);
      };
      
      const response = await retryRequest(fetchPageRequest, `${requestId}_page`);
      
      if (currentRequestIdRef.current !== requestId) {
        if (DEBUG) console.log('Запрос отменен, другой запрос был начат');
        return;
      }
      
      // Обработка различных форматов ответа API
      let pageOrders: Order[] = [];
      let totalCount = 0;
      
      if (response && typeof response === 'object') {
        if (Array.isArray(response)) {
          pageOrders = response;
          totalCount = response.length;
        } else if ('results' in response && Array.isArray(response.results)) {
          pageOrders = response.results;
          totalCount = response.count || pageOrders.length;
        } else if ('data' in response && Array.isArray(response.data)) {
          pageOrders = response.data;
          totalCount = response.total || pageOrders.length;
        }
      }
      
      if (pageOrders.length === 0) {
        throw new APIError({
          code: ERROR_CODES.UNKNOWN_ERROR,
          message: 'Неожиданный формат данных от API'
        });
      }
      
      if (DEBUG) console.log(`Получено ${pageOrders.length} заказов (всего: ${totalCount})`);
      
      // Дополнительная проверка перед обновлением состояния
      const currentlyHasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
      if (currentlyHasActiveFilters) {
        if (DEBUG) console.log('loadOrderPage: Обнаружены активные фильтры после завершения запроса, данные не будут обновлены');
        return;
      }
      
      setOrders(pageOrders);
      
      if (totalCount) {
        setTotalOrdersCount(totalCount);
      }
      
      setIsInitialLoadDone(true);
      initialLoadCompletedRef.current = true;
    } catch (error: any) {
      handleApiError(error, 'loadOrderPage');
    } finally {
      if (currentRequestIdRef.current === requestId) {
        currentRequestIdRef.current = null;
      }
      
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsActionLocked(false);
      }, 300);
    }
  }, [businessId, generateRequestId, retryRequest, handleApiError, DEBUG]);
  
  // Обновление данных
  const refreshOrders = useCallback(async (forceRefresh: boolean = false, forceAllOrders: boolean = false) => {
    if (isLoadingRef.current || isLoading) {
      if (DEBUG) console.log('Обновление уже выполняется, запрос игнорируется');
      return;
    }
    
    if (forceRefresh) {
      if (DEBUG) console.log('Принудительное обновление: очистка кеша и состояния');
      setAllOrdersLoaded(false);
      setIsInitialLoadDone(false);
      initialLoadCompletedRef.current = false;
      clearCache();
    }
    
    currentRequestIdRef.current = null;
    
    // Добавлена проверка forceAllOrders - если true, всегда загружаем все заказы
    if (forceAllOrders) {
      if (DEBUG) console.log('Принудительная загрузка всех заказов');
      await loadAllOrders();
      return;
    }
    
    // По умолчанию загружаем все данные
    await loadAllOrders();
  }, [loadAllOrders, isLoading, clearCache, DEBUG]);
          
  // Экспорт в CSV с корректной локальной датой
  const exportOrdersToCSV = useCallback(async (ordersToExport: Order[]): Promise<boolean> => {
    if (isActionLocked) {
      console.warn(t('logs.action_in_progress'));
      return false;
    }
    
    setIsActionLocked(true);
    
    try {
      if (DEBUG) console.log(`Экспорт ${ordersToExport.length} заказов в CSV`);
      
      // Определяем заголовки CSV
      const headers = [
        'ID',
        'Дата создания',
        'Имя клиента',
        'Телефон клиента',
        'Статус',
        'Платформа',
        'Сумма заказа'
      ];
      
      // Функция форматирования даты
      const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          return dateString;
        }
      };
      
      // Функция для перевода статуса
      const getStatusText = (status: string): string => {
        const statusMap: Record<string, string> = {
          'new': t('order_status.new', { defaultValue: 'Новый' }),
          'confirmed': t('order_status.confirmed', { defaultValue: 'Подтвержден' }),
          'preparing': t('order_status.preparing', { defaultValue: 'Готовится' }),
          'delivering': t('order_status.delivering', { defaultValue: 'Доставляется' }),
          'completed': t('order_status.completed', { defaultValue: 'Выполнен' }),
          'cancelled': t('order_status.cancelled', { defaultValue: 'Отменен' })
        };
        
        return statusMap[status] || status;
      };
      
      // Экранирование специальных символов для CSV
      const escapeCSV = (value: any): string => {
        if (value === null || value === undefined) return '';
        
        const stringValue = String(value);
        
        // Если строка содержит запятые, кавычки или переносы строк, заключаем в кавычки
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          // Заменяем кавычки на двойные кавычки
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        
        return stringValue;
      };
      
      // Создаём строки с данными - с учетом фактической структуры Order
      const rows = ordersToExport.map(order => [
        escapeCSV(order.id),
        escapeCSV(formatDate(order.created_at)),
        escapeCSV(order.client_name || ''),
        escapeCSV(order.client_phone || ''),
        escapeCSV(getStatusText(order.status)),
        escapeCSV(order.platform || ''),
        escapeCSV(order.total_cost || (order.cost_data && order.cost_data.total ? order.cost_data.total : 0))
      ]);
      
      // Добавляем заголовки в начало
      const csvContent = [headers, ...rows];
      
      // Преобразуем массив в строку CSV
      const csvString = csvContent.map(row => row.join(',')).join('\n');
      
      // Добавляем BOM для корректного отображения кириллицы в Excel
      const BOM = '\uFEFF';
      const csvStringWithBOM = BOM + csvString;
      
      // Получаем текущую локальную дату для имени файла
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const hours = String(currentDate.getHours()).padStart(2, '0');
      const minutes = String(currentDate.getMinutes()).padStart(2, '0');
      const fileName = `orders_export_${year}-${month}-${day}_${hours}-${minutes}.csv`;
      
      // Создаем Blob с данными
      const blob = new Blob([csvStringWithBOM], {type: 'text/csv;charset=utf-8;'});
      
      // Создаем URL для Blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Создаем временный элемент <a>
      const tempAnchor = document.createElement('a');
      
      // Добавляем атрибуты через прямое присваивание (обходя типизацию)
      // @ts-ignore - игнорируем проверки типов для следующих строк
      tempAnchor.href = blobUrl;
      // @ts-ignore
      tempAnchor.download = fileName;
      // @ts-ignore
      tempAnchor.style = 'display: none';
      
      // Добавляем элемент в DOM, кликаем по нему и удаляем
      // @ts-ignore
      document.body.appendChild(tempAnchor);
      // @ts-ignore
      tempAnchor.click();
      // @ts-ignore
      document.body.removeChild(tempAnchor);
      
      // Освобождаем URL ресурса
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Ошибка при экспорте заказов:', error);
      return false;
    } finally {
      setTimeout(() => {
        setIsActionLocked(false);
      }, 1000);
    }
  }, [t, isActionLocked, DEBUG]);
  
  // Базовая инициализация при монтировании
  useEffect(() => {
    if (!initialLoadCompletedRef.current && !isLoadingRef.current && businessId && !isInitialLoadDone) {
      // Проверяем состояние фильтров
      const hasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
      
      if (initialOrders.length > 0) {
        if (DEBUG) console.log('Используем переданные начальные данные');
        setOrders(initialOrders);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        setTotalOrdersCount(initialOrders.length);
      } else if (orderCache.hasValidCache(businessId)) {
        const cachedOrders = orderCache.getOrders(businessId);
        if (cachedOrders && cachedOrders.length > 0) {
          if (DEBUG) console.log(`Используем ${cachedOrders.length} заказов из кэша для начальной загрузки`);
          setOrders(cachedOrders);
          setAllOrdersLoaded(true);
          setIsInitialLoadDone(true);
          initialLoadCompletedRef.current = true;
          setTotalOrdersCount(cachedOrders.length);
          return;
        }
      } else if (hasActiveFilters) {
        // Если активны фильтры, загружаем всех заказов вместо страницы
        if (DEBUG) console.log('Загружаем все заказы для фильтрации при инициализации');
        loadAllOrders();
      } else {
        // Загружаем данные со смещением 0 и лимитом по умолчанию (первая страница)
        loadOrderPage(0, 10);
      }
    }
  }, [businessId, initialOrders, loadOrderPage, loadAllOrders, isInitialLoadDone, DEBUG]);
  
  // Получение информации о кеше
  const cacheInfo = useMemo(() => {
    return orderCache.getCacheInfo();
  }, [orders]); // Обновляем при изменении списка заказов
  
  return {
    orders,
    isLoading,
    error,
    allOrdersLoaded,
    totalOrdersCount,
    loadAllOrders,
    loadOrderPage,
    refreshOrders,
    exportOrdersToCSV,
    clearCache,
    cacheInfo
  };
};